import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"

import {
  Row,
  Col,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap"

//redux
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import withRouter from "components/Common/withRouter"

// Formik validation
import * as Yup from "yup"
import { useFormik } from "formik"

// actions

// import images
import logo from "assets/images/logo.svg"
import { useTranslation } from "react-i18next"
import { login } from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"
import loginBg from "assets/images/login/login-bg.webp"
import SAR_logo from "assets/images/login/sar-logo-2.svg"
import alyamama_logo from "assets/images/login/alyamama_logo.svg"
import rightVector from "assets/images/login/login_right_vector.webp"
import { ChevronDown } from "lucide-react"
import {
  arabicEnglishDigitRegex,
  convertArabicToNumber,
  recaptchaSiteJKey,
  websiteDomain,
} from "components/Common/Utils"
import ReCAPTCHA from "react-google-recaptcha"

const Login = props => {
  const { i18n, t } = useTranslation()

  //meta title
  document.title = t("Login | Yamama")

  const recaptchaRef = useRef()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, isLoading } = useSelector(state => state.Login)
  const [loginType, setLoginType] = useState("id")
  const [searchParams] = useSearchParams()
  const lang = searchParams.get("lang")

  // SET QUERY PARAMETER LANGUAGE IF HAVE IN URL
  useEffect(() => {
    if (lang) {
      const newLanguage = lang === "en" ? "en" : "sa"
      i18n.changeLanguage(newLanguage)
      // Set the initial direction based on saved language
      if (newLanguage === "sa") {
        document.body.setAttribute("dir", "rtl")
      } else {
        document.body.setAttribute("dir", "ltr")
      }
      localStorage.setItem("I18N_LANGUAGE", newLanguage)
    } else {
      const savedLanguage = localStorage.getItem("I18N_LANGUAGE") || "en"
      i18n.changeLanguage(savedLanguage)

      // Set the initial direction based on saved language
      if (savedLanguage === "sa") {
        document.body.setAttribute("dir", "rtl")
      } else {
        document.body.setAttribute("dir", "ltr")
      }
    }
  }, [])

  const [captchaValue, setCaptchaValue] = useState(null)

  const handleCaptchaChange = value => {
    setCaptchaValue(value)
  }

  const toggleLoginType = () => {
    setLoginType(loginType === "id" ? "mobile" : "id")
  }

  const validation = useFormik({
    // enableReinitialize : use this  flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      phone: "",
      password: "",
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .required(t("Field is required"))
        .test(
          "len",
          loginType === "id"
            ? t("ID number must be exactly 10 digits")
            : t("Please make sure to enter a 9 or 10-digit mobile number"),
          val => {
            if (loginType === "id") {
              return val && val.length === 10 && /^\d+$/.test(val)
            } else {
              return (
                val &&
                val.length >= 9 &&
                val.length <= 10 &&
                arabicEnglishDigitRegex.test(val)
              )
            }
          }
        ),
      password: Yup.string()
        .required(t("Password is required"))
        .min(8, t("Password must be at least 8 characters long")),
      // .matches(/[A-Z]/, t("Must contain an uppercase letter (A-Z)"))
      // .matches(/[a-z]/, t("Must contain a lowercase letter (a-z)"))
      // .matches(/\d/, t("Must contain a number (0-9)"))
      // .matches(
      //   /[!@#$%^&*(),.?":{}|<>]/,
      //   t("Must contain a special character (@, #, !, ...)")
      // ),
    }),
    onSubmit: values => {
      // if (!captchaValue) {
      //   return dispatch(
      //     showAlert({ text: t("Please complete the captcha"), type: "error" })
      //   )
      // }

      const { phone, password } = values
      const payload = {
        login_type: loginType === "id" ? "national_id" : "phone_number", // national_id and phone_number
        national_id: convertArabicToNumber(phone),
        phone_code: "+966",
        phone_number: convertArabicToNumber(phone),
        password: password,
      }
      dispatch(login(payload))
        .then(action => {
          const { statusCode, success, message, data } = action?.payload
          if (statusCode === 200 && success) {
            const {
              is_phone_verified,
              user_uuid,
              sessionId,
              phone_number,
              user_type,
            } = data || {}
            if (is_phone_verified) {
              dispatch(showAlert({ text: message, type: "success" }))
              if (user_type === "superadmin" || user_type === "staff") {
                navigate("/dashboard")
              } else {
                navigate("/profile")
              }
            } else {
              navigate(
                `/verify-and-login?user_uuid=${user_uuid}&sessionId=${sessionId}&phone_number=${phone_number}`
              )
            }
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(err => {})
    },
  })

  const LoginProperties = createSelector(
    state => state.Login,
    login => ({
      error: login.error,
    })
  )

  const { error } = useSelector(LoginProperties)

  return (
    <React.Fragment>
      <div className="login_page  h_100 overflow-x-hidden">
        <Container fluid className="h_100">
          <Row
            // style={{
            //   backgroundImage: "url('" + loginBg + "')",
            // }}
            className="bg_991"
          >
            <Col lg={6} className="px-0 r_no">
              <div className="left_section position-relative">
                <img src={loginBg} className="login_bg" alt="" />
                <div className="login_footer">
                  <Link to={"/login"}>
                    <img src={SAR_logo} width={110} height={61} alt="" />
                    <img src={alyamama_logo} width={110} height={61} />
                  </Link>
                </div>
              </div>
            </Col>
            <Col lg={6} className="px-0">
              <div className="right_section g_center h-100 position-relative">
                <img src={rightVector} alt="" className="login_right_vector" />
                <div className="form_inner">
                  <div className="login_top">
                    <Link to={"/login"}>
                      <img src={SAR_logo} width={110} height={61} alt="" />
                      <img src={alyamama_logo} width={110} height={61} />
                    </Link>
                  </div>
                  <h1>
                    {t(
                      "Seasonal Crowd Management Jobs in Al Mashaaer Al Mugaddassah Metro Line "
                    )}{" "}
                    {/* <span>{t("holy sites")}</span> */}
                  </h1>
                  <p>
                    {t("Don't have an account?")}{" "}
                    <Link
                      to={`${websiteDomain}/apply-job/`}
                      className="text-primary text-decoration-underline"
                      target="_blank"
                    >
                      {t("Click to create a new account")}
                    </Link>
                  </p>
                  <Form
                    className="form-horizontal"
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                  >
                    {error ? <Alert color="danger">{error}</Alert> : null}

                    <div className="mb-3 mt-3 form_field">
                      <Label className="form-label form_label">
                        {loginType === "id"
                          ? t("National Id")
                          : t("Mobile Number")}
                        <span className="text_danger">*</span>
                      </Label>

                      <div className="d-flex align-items-start gap-3">
                        {loginType === "mobile" && (
                          <div className="position-relative">
                            <select name="" id="" className="min_90 ltr">
                              <option value="+966">+966</option>
                            </select>
                            <ChevronDown className="select_arrow" />
                          </div>
                        )}

                        <div className="w-100">
                          <Input
                            name="phone"
                            className="form-control"
                            placeholder={
                              loginType === "id"
                                ? t("National Id")
                                : t("500000000")
                            }
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.phone || ""}
                            invalid={
                              !!(
                                validation.touched.phone &&
                                validation.errors.phone
                              )
                            } // Ensure invalid prop is a boolean
                          />

                          <FormFeedback className="d-block">
                            {validation.errors.phone}
                          </FormFeedback>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label className="form-label form_label">
                        {t("Password")}
                        <span className="text_danger">*</span>
                      </Label>

                      <Input
                        name="password"
                        value={validation.values.password || ""}
                        type="password"
                        placeholder={t("Password")}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.password &&
                          validation.errors.password
                            ? true
                            : false
                        }
                      />
                      {validation.touched.password &&
                      validation.errors.password ? (
                        <FormFeedback>
                          {validation.errors.password}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div>
                      <Link
                        to="/forgot-password"
                        className="forget_password d-block  ms-auto"
                      >
                        {t("Forgot your password?")}
                      </Link>
                    </div>
                    {/* <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaSiteJKey}
                      onChange={handleCaptchaChange}
                    /> */}

                    <div className="mt-3 d-grid ">
                      <button
                        className={`main_btn ${
                          !validation.isValid ? "disabled-button" : ""
                        }`}
                        type="submit"
                        disabled={isLoading}
                      >
                        {t("Sign in")}
                      </button>
                      <p className="privacy_text">
                        {t("By using this site, you agree to comply with our")}
                        <Link to={"/terms-conditions"}>
                          <span className="text_primary text-decoration-underline">
                            {" "}
                            {t("Terms and Conditions and Privacy Policy.")}
                          </span>
                        </Link>
                      </p>
                    </div>

                    <div className="text-center mt-3">
                      <button
                        type="button"
                        className="btn btn-link login_with_text"
                        onClick={toggleLoginType}
                      >
                        {loginType === "id"
                          ? t("Login with Mobile Number")
                          : t("Login with National Id")}
                      </button>
                    </div>
                  </Form>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
